import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { employeePortalAPI } from '../utils/api';
import { initEmployeePushNotifications, unregisterEmployeePush } from '../utils/pushNotifications';
import type { 
  EmployeeLoginRequest, 
  EmployeeChangePasswordRequest 
} from '../types/employeePortal';

interface EmployeeUser {
  id: number;
  emp_id: string;
  name: string;
  company_id: string;
  timezone?: string | null;
}

interface EmployeeAuthContextType {
  employee: EmployeeUser | null;
  mustChangePassword: boolean;
  loading: boolean;
  error: string | null;
  login: (credentials: EmployeeLoginRequest) => Promise<void>;
  logout: () => void;
  changePassword: (data: EmployeeChangePasswordRequest) => Promise<void>;
  clearError: () => void;
}

const EmployeeAuthContext = createContext<EmployeeAuthContextType | undefined>(undefined);

export const useEmployeeAuth = () => {
  const context = useContext(EmployeeAuthContext);
  if (context === undefined) {
    throw new Error('useEmployeeAuth must be used within an EmployeeAuthProvider');
  }
  return context;
};

// Only log in development
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: any[]) => {
  if (isDev) console.log(message, ...args);
};

export const EmployeeAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('auth');
  const [employee, setEmployee] = useState<EmployeeUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const extractErrorMessage = useCallback((err: any): string => {
    if (err.response?.data) {
      const data = err.response.data;
      if (data.message) return data.message;
      if (data.errors) {
        const firstFieldErrors = Object.values(data.errors)[0];
        if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
          return firstFieldErrors[0];
        }
      }
    }
    if (err.message) return err.message;
    return t('employeePortal.unexpectedError');
  }, [t]);

  useEffect(() => {
    // Restore session from localStorage
    const storedUser = localStorage.getItem('employee_user');
    const token = localStorage.getItem('employee_jwttoken');
    const storedMustChange = localStorage.getItem('employee_must_change_password');
    
    if (storedUser && token) {
      try {
        const restoredEmployee = JSON.parse(storedUser) as EmployeeUser;
        const storedTimezone = localStorage.getItem('employee_timezone');
        setEmployee({
          ...restoredEmployee,
          timezone: storedTimezone || restoredEmployee.timezone || null,
        });
        setMustChangePassword(storedMustChange === 'true');
        log('ðŸ‘¤ Employee session restored');
        initEmployeePushNotifications();
      } catch (e) {
        log('âŒ Failed to restore employee session');
        localStorage.removeItem('employee_user');
        localStorage.removeItem('employee_jwttoken');
        localStorage.removeItem('employee_refresh_token');
        localStorage.removeItem('employee_must_change_password');
        localStorage.removeItem('employee_timezone');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (credentials: EmployeeLoginRequest) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await employeePortalAPI.login(credentials);
      
      const isSuccess = response.success === true || response.success === 1 || response.success === 'true';
      if (isSuccess && response.employee && response.token) {
        const employeeWithTimezone: EmployeeUser = {
          ...response.employee,
          timezone: response.timezone ?? response.employee.timezone ?? null,
        };
        localStorage.setItem('employee_user', JSON.stringify(employeeWithTimezone));
        if (employeeWithTimezone.timezone) {
          localStorage.setItem('employee_timezone', employeeWithTimezone.timezone);
        } else {
          localStorage.removeItem('employee_timezone');
        }
        localStorage.setItem('employee_jwttoken', response.token);
        localStorage.setItem('employee_refresh_token', response.refresh_token);
        localStorage.setItem('employee_must_change_password', String(response.must_change_password));
        
        setEmployee(employeeWithTimezone);
        setMustChangePassword(response.must_change_password);
        log('âœ… Employee login successful');
        initEmployeePushNotifications();
      } else {
        const message = response.message || t('employeePortal.loginFailed');
        setError(message);
        throw new Error(message);
      }
    } catch (err: any) {
      log('âŒ Employee login error:', err);
      setError(extractErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage, t]);

  const logout = useCallback(() => {
    unregisterEmployeePush();
    localStorage.removeItem('employee_user');
    localStorage.removeItem('employee_jwttoken');
    localStorage.removeItem('employee_refresh_token');
    localStorage.removeItem('employee_must_change_password');
    localStorage.removeItem('employee_timezone');
    setEmployee(null);
    setMustChangePassword(false);
    log('ðŸ‘‹ Employee logged out');
  }, []);

  const changePassword = useCallback(async (data: EmployeeChangePasswordRequest) => {
    try {
      setLoading(true);
      setError(null);
      await employeePortalAPI.changePassword(data);
      setMustChangePassword(false);
      localStorage.setItem('employee_must_change_password', 'false');
      log('âœ… Employee password changed');
    } catch (err: any) {
      log('âŒ Change password error:', err);
      setError(extractErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [extractErrorMessage]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    employee,
    mustChangePassword,
    loading,
    error,
    login,
    logout,
    changePassword,
    clearError,
  };

  return <EmployeeAuthContext.Provider value={value}>{children}</EmployeeAuthContext.Provider>;
};




